// frontend/src/components/detail/RelatedJobsForProduct.jsx
import React from 'react';
import { Box, Typography } from '@mui/material';
import RelatedSectionShell, { TilesGrid } from './RelatedSectionShell';
import { slugify } from '../../utils/slug';
import JobBadgeTile from './tiles/JobBadgeTile';
import JobsTable from './tables/JobsTable';

/**
 * Build job counts ONLY from interviews where the story-level solution_choice_category
 * matches the current product (slug-insensitive).
 *
 * - Filter stories: s.solution_choice_category === product.
 * - For each matching story, read all s.jobs_to_be_done[].job_to_be_done_category.
 * - Count each job category once per interview (per-interview de-dupe).
 * - Ranking: occurrences (desc).
 * - Avg product satisfaction for a job = average of story-level satisfaction_level across
 *   interviews that include that job category.
 * - Share (%) = occurrences / total matching interviews.
 * - instances[] for each job: { name, date, segmentation, chosenSolution, importance, satisfaction, summary, href }
 */
function buildFromStories(stories = [], productName) {
  const target = slugify(productName || '');
  const matches = (stories || []).filter(
    (s) => s?.solution_choice_category && slugify(s.solution_choice_category) === target
  );

  const totalInterviews = matches.length;
  if (!totalInterviews) {
    return { rows: [], top3: [], sectionTotal: 0 };
  }

  const jobMap = new Map(); // jobSlug -> { name, count, sumSat, nSat, instances: [] }

  matches.forEach((s) => {
    const jobs = Array.isArray(s?.jobs_to_be_done) ? s.jobs_to_be_done : [];
    const seenThisInterview = new Set(); // de-dupe same job cat within an interview
    const storySat = Number(s?.satisfaction_level);
    const hasStorySat = Number.isFinite(storySat);

    jobs.forEach((j) => {
      const name = j?.job_to_be_done_category;
      if (!name) return;
      const key = slugify(name);
      if (seenThisInterview.has(key)) return;
      seenThisInterview.add(key);

      if (!jobMap.has(key)) {
        jobMap.set(key, { name, count: 0, sumSat: 0, nSat: 0, instances: [] });
      }
      const g = jobMap.get(key);
      g.count += 1;
      if (hasStorySat) {
        g.sumSat += storySat;
        g.nSat += 1;
      }

      // Instance (sub-row) fields
      const instance = {
        name: s.customer_name || 'Interview',
        date: s.conversation_date || '',
        segmentation: s.segmentation_data || {},
        chosenSolution: s.solution_choice_category || '',
        importance: Number(j?.importance ?? ''),
        satisfaction: Number(j?.satisfaction ?? ''),
        summary: j?.satisfaction_justification || '',
        href: `/conversations?search=${encodeURIComponent(s.customer_name || '')}`,
      };

      g.instances.push(instance);
    });
  });

  const rows = [...jobMap.values()].map((g) => {
    const avgProductSat = g.nSat ? g.sumSat / g.nSat : null;
    const pct = Math.round((g.count / totalInterviews) * 100);
    return {
      name: g.name,
      count: g.count,
      pct,
      avgProductSat,
      href: `/job/${slugify(g.name)}`,
      // new:
      instances: g.instances.sort((a, b) => {
        const ta = Date.parse(a.date || '') || 0;
        const tb = Date.parse(b.date || '') || 0;
        return tb - ta; // newest first
      }),
    };
  });

  // Rank by occurrences desc
  rows.sort((a, b) => b.count - a.count || a.name.localeCompare(b.name));

  // Build tiles for Top-3
  const top3 = rows.slice(0, 3).map((r) => ({
    title: r.name,
    count: r.count,
    pct: r.pct,
    avgProductSat: r.avgProductSat,
    href: r.href,
  }));

  return { rows, top3, sectionTotal: totalInterviews };
}

export default function RelatedJobsForProduct({ productName, customerStories = [] }) {
  const memo = React.useMemo(
    () => buildFromStories(customerStories, productName),
    [customerStories, productName]
  );

  if (!memo.rows.length) return null;

  const section = {
    title: `Jobs this product is used for`,
    description: `Based on interviews where “${productName}” is the chosen solution.`,
    section_total_interviews: memo.sectionTotal,
  };

  return (
    <RelatedSectionShell section={section}>
      {/* Top-3 tiles */}
      <Box sx={{ mb: 2 }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
          Top Jobs (by occurrences)
        </Typography>
        <TilesGrid
          tiles={memo.top3}
          renderTile={(t) => (
            <JobBadgeTile
              title={t.title}
              count={t.count}
              pct={t.pct}
              avgProductSat={t.avgProductSat}
              href={t.href}
            />
          )}
        />
      </Box>

      {/* Full table (expandable) */}
      <Box sx={{ mt: 1 }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
          All Jobs for this Product
        </Typography>
        <JobsTable rows={memo.rows} />
      </Box>
    </RelatedSectionShell>
  );
}
